import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*

// Sdílené pro statistiky týmu i hráče: model per-hra, loader (3 zdroje) a znovupoužitelné grafy.

data class StatGame(
    val playerId: String,
    val source: String,      // league | training | tournament
    val gameType: String,
    val score: Int,
    val date: String?,
    val seasonId: String?,
    val sessionKey: String,  // seskupení do 1 bodu (zápas / trénink / turnaj)
    val refId: String,       // training_id / tournament_id / match_id (pro klíč zápisu)
    val gameNo: Int,
    val round: Int? = null,  // číslo kola (jen liga)
) {
    fun sheetKey(): String = "$source:$refId:$gameNo"
    fun sessionLabel(): String = when (source) { "league" -> "kolo"; "training" -> "trénink"; else -> "turnaj" }
    /** Popisek hry: zdroj [· kolo (liga)] · hra N [· datum]. */
    fun label(): String = buildList {
        add(sourceLabelSingular(source))
        if (source == "league" && round != null) add("$round. kolo")
        add("hra $gameNo")
        date?.let { add(it) }
    }.joinToString(" · ")
}

/** "Sezónní rok" data podle předělu 1.7.: leden–červen patří k sezóně začínající předchozí červenec
 *  (sezóna Y/Y+1 = 1.7.Y – 30.6.Y+1). Používá se k přiřazení tréninků/turnajů (mají jen datum, ne
 *  seasonId z bowlingligy) do stejné sezóny jako ligové zápasy. */
fun seasonYearOf(dateIso: String): Int? {
    if (dateIso.length < 7) return null
    val y = dateIso.substring(0, 4).toIntOrNull() ?: return null
    val m = dateIso.substring(5, 7).toIntOrNull() ?: return null
    return if (m >= 7) y else y - 1
}

/** seasonId -> sezónní rok (viz seasonYearOf), odvozeno z data libovolného ligového zápasu té sezóny. */
fun seasonYears(games: List<StatGame>): Map<String, Int> =
    games.asSequence()
        .filter { it.source == "league" && it.seasonId != null && it.date != null }
        .distinctBy { it.seasonId }
        .mapNotNull { g -> seasonYearOf(g.date!!)?.let { g.seasonId!! to it } }
        .toMap()

/** Patří hra do vybrané sezóny? Ligové hry mají vlastní seasonId; tréninky/turnaje se přiřadí podle
 *  data přes seasonYearOf (předěl 1.7.), aby se při filtru na sezónu neprolínaly mezi ročníky. */
fun StatGame.matchesSeason(selectedSeason: String?, seasonYearById: Map<String, Int>): Boolean {
    if (selectedSeason == null) return true
    if (source == "league") return seasonId == selectedSeason
    val targetYear = seasonYearById[selectedSeason] ?: return true // neznámý rok sezóny -> nefiltrovat
    val y = date?.let { seasonYearOf(it) } ?: return false
    return y == targetYear
}

val SOURCE_LABELS = listOf("league" to "Liga", "training" to "Tréninky", "tournament" to "Turnaje")
fun sourceLabel(code: String) = SOURCE_LABELS.firstOrNull { it.first == code }?.second ?: code
fun sourceLabelSingular(code: String) = when (code) { "league" -> "Liga"; "training" -> "Trénink"; else -> "Turnaj" }
fun sourceColor(code: String) = when (code) { "training" -> "#2e9e5b"; "tournament" -> "#378add"; else -> "#f5b301" }
fun round1(x: Double) = (x * 10).toInt() / 10.0
fun <T> Set<T>.toggle(item: T): Set<T> = if (item in this) this - item else this + item

/** Načte a sloučí per-hra skóre ze všech tří zdrojů + seznam ligových sezón. */
suspend fun loadStatGames(token: String): Pair<List<StatGame>, List<Pair<String, String>>> {
    val lg = Api.fetchTeamPlayerGames()
    val seasons = lg.flatMap { it.games }.map { it.seasonId to it.seasonName }.distinct().sortedByDescending { it.second }
    val league = lg.flatMap { p ->
        p.games.map { g -> StatGame(p.playerId, "league", "classic", g.score, g.date, g.seasonId, "L:${g.seasonId}:${g.phase}:${g.roundNumber}", g.matchId, g.gameNo, g.roundNumber) }
    }
    val trainings = Api.fetchTrainings(token)
    val trDate = trainings.associate { it.id to it.date }
    val train = Api.fetchTrainingScores(token, trainings.map { it.id })
        .map { s -> StatGame(s.blPlayerId, "training", s.gameType, s.score, trDate[s.trainingId], null, "T:${s.trainingId}", s.trainingId ?: "", s.gameNo) }
    val tours = Api.fetchTournaments(token)
    val toDate = tours.associate { it.id to it.date }
    val tour = Api.fetchTournamentScores(token, tours.map { it.id })
        .map { s -> StatGame(s.blPlayerId, "tournament", s.gameType, s.score, toDate[s.tournamentId], null, "U:${s.tournamentId}", s.tournamentId ?: "", s.gameNo) }
    return (league + train + tour) to seasons
}

@Composable
fun Chip(label: String, on: Boolean, onToggle: () -> Unit) {
    Button(attrs = {
        classes(*(listOfNotNull("chip", "chip-on".takeIf { on }).toTypedArray()))
        onClick { onToggle() }
    }) { Text(label) }
}

@Composable
fun StatPill2(value: String, label: String) {
    Div(attrs = { classes("stat-pill") }) {
        Span(attrs = { classes("stat-value") }) { Text(value) }
        Span(attrs = { classes("stat-label") }) { Text(label) }
    }
}

@Composable
fun BarChart(rows: List<Triple<String, Double, Int>>) {
    if (rows.isEmpty()) return
    val maxV = rows.maxOf { it.second }.coerceAtLeast(1.0)
    Div(attrs = { classes("bar-chart") }) {
        rows.forEach { (label, value, count) ->
            Div(attrs = { classes("bar-row") }) {
                Div(attrs = { classes("bar-label") }) { Text(label) }
                Div(attrs = { classes("bar-track") }) {
                    val pct = (value / maxV * 100).toInt().coerceIn(6, 100)
                    Div(attrs = { classes("bar-fill"); attr("style", "width:${pct}%") }) { Text("Ø $value") }
                }
                Div(attrs = { classes("bar-count") }) { Text("$count her") }
            }
        }
    }
}

data class TimePoint(val date: String, val avg: Double, val source: String, val label: String)

/** Body do grafu času (session = 1 bod = průměr té session). */
fun timePoints(games: List<StatGame>): List<TimePoint> =
    games.filter { it.date != null }
        .groupBy { it.sessionKey }
        .map { (_, gs) -> TimePoint(gs.first().date!!, round1(gs.map { it.score }.average()), gs.first().source, gs.first().sessionLabel()) }
        .sortedBy { it.date }

@Composable
fun TimeChart(points: List<TimePoint>, evenlySpaced: Boolean = false, average: Double? = null) {
    if (points.size < 2) {
        P(attrs = { classes("muted") }) { Text("Málo dat pro graf (min. 2 session).") }
        return
    }
    val svg = buildTimeChartSvg(points, evenlySpaced, average)
    Div(attrs = { classes("time-chart-legend") }) {
        average?.let {
            Span(attrs = { classes("legend-item") }) {
                Span(attrs = { classes("legend-average") }) {}
                Text("Průměr ${round1(it)}")
            }
        }
        SOURCE_LABELS.forEach { (code, label) ->
            if (points.any { it.source == code }) {
                Span(attrs = { classes("legend-item") }) {
                    Span(attrs = { classes("legend-dot"); attr("style", "background:${sourceColor(code)}") }) {}
                    Text(label)
                }
            }
        }
    }
    key(svg) {
        Div(attrs = {
            classes("time-chart")
            ref { el -> el.innerHTML = svg; onDispose { } }
        }) {}
    }
}

// Paleta linek pro porovnání hráčů; průměr týmu má vlastní zvýrazněnou barvu.
val PLAYER_COLORS = listOf("#f5b301", "#e0655f", "#4a9de0", "#3fb984", "#b98cd8", "#e0a34a", "#5fd0c0", "#d86fa0")
fun playerColor(index: Int) = PLAYER_COLORS[index % PLAYER_COLORS.size]
const val TEAM_AVG_COLOR = "#e8eaed"

data class ChartSeries(val name: String, val color: String, val points: List<TimePoint>, val emphasize: Boolean = false)

/** Graf vývoje v čase s více linkami (např. průměr týmu + jednotliví hráči). */
@Composable
fun MultiTimeChart(series: List<ChartSeries>) {
    val all = series.flatMap { it.points }
    if (all.size < 2) {
        P(attrs = { classes("muted") }) { Text("Málo dat pro graf (min. 2 session).") }
        return
    }
    Div(attrs = { classes("time-chart-legend") }) {
        series.forEach { s ->
            if (s.points.isNotEmpty()) Span(attrs = { classes("legend-item") }) {
                Span(attrs = { classes("legend-dot"); attr("style", "background:${s.color}") }) {}
                Text(s.name)
            }
        }
    }
    val svg = buildMultiChartSvg(series)
    key(svg) {
        Div(attrs = {
            classes("time-chart")
            ref { el -> el.innerHTML = svg; onDispose { } }
        }) {}
    }
}

private fun buildMultiChartSvg(series: List<ChartSeries>): String {
    val w = 640.0; val h = 240.0; val left = 44.0; val right = w - 16.0; val top = 16.0; val bottom = h - 30.0
    val all = series.flatMap { it.points }
    val times = all.map { kotlin.js.Date(it.date).getTime() }
    val tmin = times.minOrNull()!!; val tmax = times.maxOrNull()!!
    var ymin = all.minOf { it.avg }; var ymax = all.maxOf { it.avg }
    if (ymax == ymin) { ymin -= 10; ymax += 10 } else { val pad = (ymax - ymin) * 0.12; ymin -= pad; ymax += pad }
    ymin = kotlin.math.floor(ymin / 10.0) * 10.0; ymax = kotlin.math.ceil(ymax / 10.0) * 10.0 // meze osy na celé desítky
    fun pxT(t: Double): Double = if (tmax == tmin) (left + right) / 2 else left + (right - left) * ((t - tmin) / (tmax - tmin))
    fun py(v: Double): Double = bottom - (bottom - top) * ((v - ymin) / (ymax - ymin))
    val body = series.joinToString("") { s ->
        val sp = s.points.sortedBy { kotlin.js.Date(it.date).getTime() }
        val sw = if (s.emphasize) 3 else 2
        val dash = if (s.emphasize) " stroke-dasharray='6 3'" else ""
        val poly = if (sp.size >= 2)
            "<polyline points='${sp.joinToString(" ") { "${round1(pxT(kotlin.js.Date(it.date).getTime()))},${round1(py(it.avg))}" }}' fill='none' stroke='${s.color}' stroke-width='$sw'$dash/>"
        else ""
        val dots = sp.joinToString("") { p ->
            "<circle cx='${round1(pxT(kotlin.js.Date(p.date).getTime()))}' cy='${round1(py(p.avg))}' r='3' fill='${s.color}'><title>${s.name} ${p.date}: Ø ${p.avg}</title></circle>"
        }
        poly + dots
    }
    val axis = "<line x1='$left' y1='$bottom' x2='$right' y2='$bottom' stroke='#2c3240'/>" +
        "<line x1='$left' y1='$top' x2='$left' y2='$bottom' stroke='#2c3240'/>" +
        "<text x='${left - 6}' y='${top + 4}' fill='#9aa0aa' font-size='11' text-anchor='end'>${round1(ymax)}</text>" +
        "<text x='${left - 6}' y='$bottom' fill='#9aa0aa' font-size='11' text-anchor='end'>${round1(ymin)}</text>"
    return "<svg viewBox='0 0 $w $h' width='100%' xmlns='http://www.w3.org/2000/svg'>$axis$body</svg>"
}

internal fun buildTimeChartSvg(points: List<TimePoint>, evenlySpaced: Boolean, average: Double? = null): String {
    val w = 640.0; val h = 220.0; val left = 44.0; val right = w - 16.0; val top = 16.0; val bottom = h - 30.0
    // Více her může mít stejné datum. Rozprostři je uvnitř daného dne, aby body neležely
    // přesně na sobě, ale přitom zůstalo zachované pořadí i reálné rozestupy mezi dny.
    val countsByDate = points.groupingBy { it.date }.eachCount()
    val seenByDate = mutableMapOf<String, Int>()
    val dayMs = 24.0 * 60 * 60 * 1000
    val times = if (evenlySpaced) {
        points.indices.map { it.toDouble() }
    } else {
        points.map { point ->
            val count = countsByDate[point.date] ?: 1
            val position = seenByDate.getOrElse(point.date) { 0 }
            seenByDate[point.date] = position + 1
            val offset = if (count <= 1) 0.0 else ((position + 1.0) / (count + 1.0) - 0.5) * dayMs * 0.8
            kotlin.js.Date(point.date).getTime() + offset
        }
    }
    val tmin = times.minOrNull()!!; val tmax = times.maxOrNull()!!
    var ymin = minOf(points.minOf { it.avg }, average ?: points.minOf { it.avg })
    var ymax = maxOf(points.maxOf { it.avg }, average ?: points.maxOf { it.avg })
    if (ymax == ymin) { ymin -= 10; ymax += 10 } else { val pad = (ymax - ymin) * 0.12; ymin -= pad; ymax += pad }
    ymin = kotlin.math.floor(ymin / 10.0) * 10.0; ymax = kotlin.math.ceil(ymax / 10.0) * 10.0 // meze osy na celé desítky
    fun px(i: Int): Double = if (tmax == tmin) (if (points.size == 1) (left + right) / 2 else left + (right - left) * i / (points.size - 1))
    else left + (right - left) * ((times[i] - tmin) / (tmax - tmin))
    fun py(v: Double): Double = bottom - (bottom - top) * ((v - ymin) / (ymax - ymin))
    val poly = points.indices.joinToString(" ") { "${round1(px(it))},${round1(py(points[it].avg))}" }
    val dots = points.indices.joinToString("") { i ->
        "<circle cx='${round1(px(i))}' cy='${round1(py(points[i].avg))}' r='4' fill='${sourceColor(points[i].source)}'><title>${points[i].label} ${points[i].date}: Ø ${points[i].avg}</title></circle>"
    }
    val axis = "<line x1='$left' y1='$bottom' x2='$right' y2='$bottom' stroke='#2c3240'/>" +
        "<line x1='$left' y1='$top' x2='$left' y2='$bottom' stroke='#2c3240'/>" +
        "<text x='${left - 6}' y='${top + 4}' fill='#9aa0aa' font-size='11' text-anchor='end'>${round1(ymax)}</text>" +
        "<text x='${left - 6}' y='$bottom' fill='#9aa0aa' font-size='11' text-anchor='end'>${round1(ymin)}</text>" +
        "<text x='$left' y='${h - 10}' fill='#9aa0aa' font-size='11'>${points.first().date}</text>" +
        "<text x='$right' y='${h - 10}' fill='#9aa0aa' font-size='11' text-anchor='end'>${points.last().date}</text>"
    val averageLine = average?.let {
        "<line x1='$left' y1='${round1(py(it))}' x2='$right' y2='${round1(py(it))}' stroke='#e8eaed' stroke-width='2' stroke-dasharray='7 5'><title>Průměr ${round1(it)}</title></line>"
    } ?: ""
    return "<svg viewBox='0 0 $w $h' width='100%' xmlns='http://www.w3.org/2000/svg'>$axis$averageLine<polyline points='$poly' fill='none' stroke='#f5b301' stroke-width='2'/>$dots</svg>"
}
